<template>
  <t-curtain ref="meetCurtain">
    <t-curtain-tab group="meets" name="create" :title="$t('components.networkingCurtain.newOneOnOne')" fullscreen :key="user.id">
      <div class="flex flex-col w-full h-full gap-4 px-2 pb-3" :key="`meets_inputs_${inputsKey}`">
        <div class="flex flex-col gap-2 p-2 border-2 rounded-lg border-platinum">
          <div class="flex justify-between w-full">
            <t-user-preference v-if="user.networking.preference" :title="user.networking.preference" />
            <t-user-level :level="user.networking.level" />
          </div>
          <div class="flex items-center gap-3">
            <img :src="getField('avatar')" class="w-16 h-16 border-2 rounded-full border-platinum" />
            <div class="flex flex-col">
              <t-text class="font-semibold" color="black">{{ user.fullname }}</t-text>
              <div class="flex flex-col" v-if="getField('job_title') || getField('location')">
                <t-text class="font-semibold line-clamp-2 break-word" color="black" size="md" v-if="getField('job_title')">{{
                  getField('job_title')
                }}</t-text>
                <t-text color="black" class="line-clamp-2 break-word" size="md" v-if="getField('location')">{{
                  getField('location')
                }}</t-text>
              </div>
            </div>
          </div>
        </div>
        <t-text class="font-semibold" size="xl" color="black">{{ $t('components.networkingCurtain.details') }}</t-text>
        <default-input ref="location" :label="$t('components.networkingCurtain.location')" />
        <big-input ref="title" :label="$t('components.networkingCurtain.coverMessage')" />
        <div class="flex flex-col gap-1">
          <t-text class="font-semibold" size="xl" color="black">{{ $t('components.networkingCurtain.duration') }}</t-text>
          <div class="flex items-center justify-between">
            <label class="flex items-center gap-1 rounded-lg" style="box-shadow: rgba(31, 31, 31, 0.12) 0px 5px 10px">
              <input type="radio" name="duration" class="hidden" value="15" :checked="duration === 15" />
              <t-button size="sm" @click.native="duration = 15" :active="duration === 15">
                <t-text :color="duration === 15 ? 'branded' : 'black'" class="whitespace-nowrap">{{
                  $t('components.networkingCurtain.15min')
                }}</t-text>
              </t-button>
            </label>
            <label class="flex items-center gap-1 rounded-lg" style="box-shadow: rgba(31, 31, 31, 0.12) 0px 5px 10px">
              <input type="radio" name="duration" class="hidden" value="30" :checked="duration === 30" />
              <t-button size="sm" @click.native="duration = 30" :active="duration === 30">
                <t-text :color="duration === 30 ? 'branded' : 'black'" class="whitespace-nowrap">{{
                  $t('components.networkingCurtain.30min')
                }}</t-text>
              </t-button>
            </label>
            <label class="flex items-center gap-1 rounded-lg" style="box-shadow: rgba(31, 31, 31, 0.12) 0px 5px 10px">
              <input type="radio" name="duration" class="hidden" value="45" :checked="duration === 45" />
              <t-button size="sm" @click.native="duration = 45" :active="duration === 45">
                <t-text :color="duration === 45 ? 'branded' : 'black'" class="whitespace-nowrap">{{
                  $t('components.networkingCurtain.45min')
                }}</t-text>
              </t-button>
            </label>
            <label class="flex items-center gap-1 rounded-lg" style="box-shadow: rgba(31, 31, 31, 0.12) 0px 5px 10px">
              <input type="radio" name="duration" class="hidden" value="60" :checked="duration === 60" />
              <t-button size="sm" @click.native="duration = 60" :active="duration === 60">
                <t-text :color="duration === 60 ? 'branded' : 'black'" class="whitespace-nowrap">{{
                  $t('components.networkingCurtain.60min')
                }}</t-text>
              </t-button>
            </label>
          </div>
        </div>
        <div class="relative w-full">
          <default-input
            ref="time_start"
            actAsButton
            class="z-10"
            @click.native="isDatePickerOpened = !isDatePickerOpened"
            :label="$t('components.networkingCurtain.timeSlot')"
            :value="timeSlot"
            labelClass="after:content-['*'] after:text-red"
          >
            <template v-if="!!timeSlot" v-slot:right-side>
              <ph-x :size="24" @click.native.stop="timeSlot = ''" />
            </template>
          </default-input>
          <div
            class="fixed inset-0 z-30 flex items-center justify-center overflow-hidden duration-200 rounded-lg"
            :class="{'opacity-100': isDatePickerOpened, 'opacity-0 pointer-events-none': !isDatePickerOpened}"
            :style="theme === 'light' && isDatePickerOpened ? {boxShadow: 'rgba(31, 31, 31, 0.12) 0px 5px 10px'} : {}"
          >
            <div class="absolute inset-0 z-30 bg-black/50" @click="isDatePickerOpened = false"></div>
            <date-picker
              v-model="timeSlot"
              ref="datepicker"
              valueType="format"
              :format="appTimeFormat === '24H' ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY, hh:mm A'"
              :use12h="appTimeFormat !== '24H'"
              :lang="lang"
              inline
              confirm
              confirm-text="Apply"
              :disabled-date="disablePastDates"
              type="datetime"
              @confirm="isDatePickerOpened = false"
              class="w-[360px] h-[314px] overflow-hidden border-0 rounded-lg z-40"
              :class="{'bg-platinum': theme === 'dark', 'bg-white': theme === 'light'}"
            />
          </div>
        </div>
        <t-text color="gray" class="-mt-2">Note: Time will be saved in {{ localTimezone }} timezone</t-text>
      </div>
      <template #footer>
        <t-button color="branded" @click.native="create()" ref="invitation" class="font-semibold">{{
          $t('components.networkingCurtain.sendInvitation')
        }}</t-button>
      </template>
    </t-curtain-tab>
    <t-curtain-tab
      group="meets"
      name="success"
      floating
      :fullscreen="false"
      :title="$t('components.networkingCurtain.success')"
      :historyMode="false"
    >
      <div class="flex">
        <t-text size="xl" color="black">
          {{ $t('components.networkingCurtain.invitationSent') }}
        </t-text>
      </div>
      <template #footer>
        <div class="flex items-center gap-2">
          <t-button @click.native="$router.push({name: 'networking.meets'})" class="font-semibold">{{
            $t('components.networkingCurtain.seeAll')
          }}</t-button>
          <t-button color="branded" @click.native="$refs.meetCurtain.hide()" class="font-semibold">{{
            $t('components.networkingCurtain.stayHere')
          }}</t-button>
        </div>
      </template>
    </t-curtain-tab>
  </t-curtain>
</template>

<script>
  import AXIOS from '@/integrations/axios'
  import DatePicker from 'vue2-datepicker'
  import {mapGetters} from 'vuex'
  export default {
    name: 't-meets-curtain',
    props: {
      user: {
        type: Object,
        required: true
      },
      data: {
        type: Object,
        required: false
      }
    },
    components: {
      DatePicker
    },
    computed: {
      ...mapGetters(['theme', 'appTimeFormat'])
    },
    data() {
      return {
        timeSlot: '',
        duration: 15,
        inputsKey: 0.01,
        isDatePickerOpened: false,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lang: {
          formatLocale: {}
        }
      }
    },
    created() {
      this.lang.formatLocale.months = eval(this.$t('components.daysMonths.months'))
      this.lang.formatLocale.monthsShort = eval(this.$t('components.daysMonths.monthsShort'))
      this.lang.formatLocale.weekdays = eval(this.$t('components.daysMonths.weekdays'))
      this.lang.formatLocale.weekdaysShort = eval(this.$t('components.daysMonths.weekdaysShort'))
      this.lang.formatLocale.weekdaysMin = eval(this.$t('components.daysMonths.weekdaysMin'))
    },
    methods: {
      open() {
        if (this.data) {
          if (this.data.duration) {
            this.duration = this.data.duration
          }

          if (this.data.time_start) {
            this.timeSlot = new Date(this.data.time_start * 1000).toLocaleString('en-GB', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })

            const [datePart, timePart] = this.timeSlot.replace(' at ', ', ').split(', ')
            const [time, period] = timePart.split(' ')

            this.timeSlot = `${datePart}, ${time} ${period.toUpperCase()}`
            this.$refs.time_start.focus()
          }

          if (this.data.location) {
            this.$refs.location.inputVal = this.data.location
            this.$refs.location.focus()
          }

          if (this.data.coverMessage) {
            this.$refs.title.inputVal = this.data.coverMessage
            this.$refs.title.focus()
          }
        }
        this.$refs.meetCurtain.tab('meets/create')
      },
      hide() {
        this.$refs.meetCurtain.hide()
      },
      getField(field, scope = this.user) {
        if (!scope.profile) {
          return scope[field]
        }
        const _field = scope.profile.find(el => el.name === field)
        if (_field) return _field.value
        else return false
      },
      create() {
        this.$refs.invitation.toggleLoad()
        if (this.data && this.data.id) {
          AXIOS.put(`/meet/${this.data.id}`, {
            duration: document.querySelector('input[name="duration"]:checked').value,
            time_start: Math.floor(this.$refs.datepicker.currentValue.getTime() / 1000),
            location: this.$refs.location.getValue(),
            title: this.$refs.title.getValue(),
            timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone
          })
            .then(() => {
              this.$refs.invitation.toggleLoad()
              this.$refs.meetCurtain.hide()
              this.$emit('updatePage')
            })
            .catch(err => {
              this.$refs.invitation.toggleLoad()
              err.response.data.forEach(field => {
                if (this.$refs[field.field]) this.$refs[field.field].setError(field.message)
              })
            })
          return
        }

        AXIOS.post(`/meet`, {
          user_id: this.user.id,
          duration: document.querySelector('input[name="duration"]:checked').value,
          time_start: Math.floor(this.$refs.datepicker.currentValue.getTime() / 1000),
          location: this.$refs.location.getValue(),
          title: this.$refs.title.getValue(),
          timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
          .then(() => {
            this.$refs.invitation.toggleLoad()
            setTimeout(() => {
              this.timeSlot = ''
              this.inputsKey++
            }, 200)
            if (this.data) this.$refs.meetCurtain.hide()
            else this.$refs.meetCurtain.tab('meets/success')
          })
          .catch(err => {
            this.$refs.invitation.toggleLoad()
            err.response.data.forEach(field => {
              if (this.$refs[field.field]) this.$refs[field.field].setError(field.message)
            })
          })
      },
      disablePastDates(date) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return date < today
      }
    },
    watch: {
      timeSlot(newVal) {
        if (newVal !== '' && newVal !== null) this.$refs.time_start.clearError()
      }
    }
  }
</script>
